import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from 'context/AuthContext'

const navLinks = [
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Blog', href: '/blog' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Affiliates', href: '/affiliates' },
  { label: 'Contact', href: '/contact' },
]

export default function LPNavigation() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const isHome = location.pathname === '/'
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  const handleAnchorClick = (e, href) => {
    if (href.startsWith('/#')) {
      const id = href.replace('/#', '')
      if (isHome) {
        e.preventDefault()
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 h-16 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-nav border-b border-[#E2E8F0]'
            : isHome ? 'bg-transparent' : 'bg-white/95 backdrop-blur-md border-b border-[#E2E8F0]'
        }`}
      >
        <div className="content-max h-full flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="" className="w-7 h-7" />
            <span className="font-sans font-medium text-base text-charcoal">
              Safe Dividend Tracker
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                onClick={(e) => handleAnchorClick(e, link.href)}
                className={`text-sm font-sans relative link-underline transition-colors ${
                  (link.href === '/blog' && location.pathname === '/blog') ||
                  (link.href === '/contact' && location.pathname === '/contact')
                    ? 'text-[#1B5E20]'
                    : 'text-[#475569] hover:text-[#1B5E20]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <Link
                to="/user-dashboard"
                className="text-sm font-medium bg-[#1B5E20] text-white px-5 py-2.5 rounded-full hover:bg-[#145218] hover:shadow-gold-glow hover:-translate-y-px transition-all duration-400"
              >
                Go to Dashboard →
              </Link>
            ) : (
              <>
                <a
                  href="/login"
                  className="text-sm text-slate hover:text-charcoal transition-colors link-underline"
                >
                  Sign In
                </a>
                <a
                  href="/register"
                  className="text-sm font-medium bg-[#1B5E20] text-white px-5 py-2.5 rounded-full hover:bg-[#145218] hover:shadow-gold-glow hover:-translate-y-px transition-all duration-400"
                >
                  Get Started Free
                </a>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <span className="w-5 h-0.5 rounded-full bg-charcoal" />
            <span className="w-5 h-0.5 rounded-full bg-charcoal" />
            <span className="w-5 h-0.5 rounded-full bg-charcoal" />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 bg-charcoal/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        >
          <div
            className="absolute right-0 top-0 h-full w-[80%] max-w-sm bg-warm-stone p-6 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="self-end p-2 mb-6"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M2 2L18 18M18 2L2 18" stroke="#0B0A0A" strokeWidth="1.5" />
              </svg>
            </button>
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className="text-lg text-charcoal py-2"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <div className="mt-auto flex flex-col gap-3 pt-6 border-t border-border-custom">
              {isAuthenticated ? (
                <Link
                  to="/user-dashboard"
                  className="text-center bg-[#1B5E20] text-white font-medium py-3 rounded-full"
                  onClick={() => setMobileOpen(false)}
                >
                  Go to Dashboard →
                </Link>
              ) : (
                <>
                  <a href="/login" className="text-center text-charcoal py-3">
                    Sign In
                  </a>
                  <a
                    href="/register"
                    className="text-center bg-[#1B5E20] text-white font-medium py-3 rounded-full"
                  >
                    Get Started Free
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
